import { useMemo, useState } from "react";
import Decimal from "decimal.js";
import * as XLSX from "xlsx";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";

import { useListCustomers } from "@/lib/api/customer";
import { useListLocations, type Location } from "@/lib/api/generalContract";
import type { Customer } from "@/lib/api/reports";
import { MimeType } from "@/lib/api/utils";
import { fetchApiPost } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { downloadBinaryData } from "@/lib/browser/download";
import { parseFromFile, safeParseDecimal } from "@/lib/excel";
import { ExcelComposer, type SectionConfig, type CellValue } from "@/lib/excel/composer";
import { CellFormat, numberToExcelColumn } from "@/lib/excel/common";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InputRow {
  client_id: string;
  customer_name: string;
  location_id: string;
  quantity: Decimal;
  settlement: Decimal;
}

interface CustomerAgg {
  client_id: string;
  customer_name: string;
  marketing_fee: Decimal;
  quantity_sum: Decimal;
  settlement_sum: Decimal;
}

interface LocationAgg {
  location_id: string;
  location_label: string;
  amount_to_book: Decimal;
}

interface SkippedAgg {
  type: "Missing Customer" | "Missing Marketing Fee";
  client_id: string;
  customer_name: string;
  row_count: number;
}

interface ReportSummary {
  total_input_rows: number;
  unique_customers: number;
  matched_with_fee_count: number;
  missing_customer_count: number;
  missing_marketing_fee_count: number;
}

interface ProcessResult {
  customer_aggs: CustomerAgg[];
  location_aggs: LocationAgg[];
  skipped: SkippedAgg[];
  summary: ReportSummary;
}

// Tracked-report API response (proxied through NX backend)
interface CreatedTrackedReportFile {
  document_id: string;
  bucket: string;
  key: string;
  filename: string;
  put_url: string;
}

interface CreatedTrackedReport {
  tracked_report_id: string;
  report_type: string;
  source: CreatedTrackedReportFile[];
  output: CreatedTrackedReportFile[];
}

const REPORT_TYPE = "marketing_fee_accrual";
const OUTPUT_FILENAME = "marketing_fee_accrual.xlsx";

// ---------------------------------------------------------------------------
// Input parsing
// ---------------------------------------------------------------------------

function readString(value: unknown): string {
  if (value == null) return "";
  return String(value).trim();
}

async function parseInputFile(file: File): Promise<InputRow[]> {
  const parsed = await parseFromFile(file);
  if (!parsed.success || !parsed.data) {
    throw new Error("Failed to read the input file.");
  }
  const { xlsxWorkbook } = parsed.data;
  const firstSheetName = xlsxWorkbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error("Input file has no sheets.");
  }
  const sheet = xlsxWorkbook.Sheets[firstSheetName];
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: null,
    raw: true,
  });

  return rawRows.map((row: Record<string, unknown>): InputRow => ({
    client_id: readString(row["Customer/Vendor Id"]),
    customer_name: readString(row["Customer/Vendor Name"]),
    location_id: readString(row["Location Id"]),
    quantity: safeParseDecimal(row["Quantity"] as string | number),
    settlement: safeParseDecimal(row["Settlement"] as string | number),
  }));
}

// ---------------------------------------------------------------------------
// Processing
// ---------------------------------------------------------------------------

function findCustomerByClientId(
  client_id: string,
  customers: Customer[],
): Customer | undefined {
  const target = client_id.trim().toLowerCase();
  if (!target) return undefined;
  return customers.find(
    (c: Customer) => (c.client_id ?? "").trim().toLowerCase() === target,
  );
}

function processInputRows(
  rows: InputRow[],
  customers: Customer[],
  locations: Location[],
): ProcessResult {
  // Per-client aggregates that survive into the report
  const customerAggMap = new Map<string, CustomerAgg>();
  // Per-(client_id, location_id) amount-to-book — needed for location section
  const locAccumulator = new Map<string, Decimal>();
  // Skipped trackers
  const missingCustomerMap = new Map<string, SkippedAgg>();
  const missingFeeMap = new Map<string, SkippedAgg>();

  const uniqueClientIds = new Set<string>();

  for (const row of rows) {
    if (!row.client_id) continue;
    uniqueClientIds.add(row.client_id);

    const customer = findCustomerByClientId(row.client_id, customers);

    if (!customer) {
      const existing = missingCustomerMap.get(row.client_id);
      if (existing) {
        existing.row_count += 1;
      } else {
        missingCustomerMap.set(row.client_id, {
          type: "Missing Customer",
          client_id: row.client_id,
          customer_name: row.customer_name,
          row_count: 1,
        });
      }
      continue;
    }

    if (customer.marketing_fee == null || customer.marketing_fee === "") {
      const existing = missingFeeMap.get(row.client_id);
      if (existing) {
        existing.row_count += 1;
      } else {
        missingFeeMap.set(row.client_id, {
          type: "Missing Marketing Fee",
          client_id: row.client_id,
          customer_name: customer.long_name || row.customer_name,
          row_count: 1,
        });
      }
      continue;
    }

    const fee = safeParseDecimal(customer.marketing_fee);
    const existing = customerAggMap.get(row.client_id);
    if (existing) {
      existing.quantity_sum = existing.quantity_sum.plus(row.quantity ?? 0);
      existing.settlement_sum = existing.settlement_sum.plus(row.settlement ?? 0);
    } else {
      customerAggMap.set(row.client_id, {
        client_id: row.client_id,
        customer_name: customer.long_name || row.customer_name,
        marketing_fee: fee,
        quantity_sum: row.quantity,
        settlement_sum: row.settlement,
      });
    }

    // Per-(customer, location) for the location section.
    // amount_to_book = sum(settlement for that (customer, location)) * fee.
    // We sum the settlement first, then multiply by fee at the end.
    const locKey = `${row.client_id}|${row.location_id}`;
    const existingLoc = locAccumulator.get(locKey);
    locAccumulator.set(
      locKey,
      existingLoc ? existingLoc.plus(row.settlement) : row.settlement,
    );
  }

  // Resolve the per-(customer,location) settlement sums into per-location
  // amount-to-book using the customer's marketing fee.
  const locationAggMap = new Map<string, Decimal>();
  for (const [key, settlementSum] of locAccumulator.entries()) {
    const [client_id, location_id] = key.split("|");
    const custAgg = customerAggMap.get(client_id);
    if (!custAgg) continue; // safety: shouldn't happen since we only added on success
    const amount = settlementSum.times(custAgg.marketing_fee);
    const existing = locationAggMap.get(location_id);
    locationAggMap.set(location_id, existing ? existing.plus(amount) : amount);
  }

  const customer_aggs = Array.from(customerAggMap.values()).sort(
    (a: CustomerAgg, b: CustomerAgg) =>
      a.customer_name.localeCompare(b.customer_name),
  );

  const locationLabelByExternalId = new Map<string, string>();
  for (const loc of locations) {
    const externalId = (loc.external_id ?? "").trim();
    if (!externalId) continue;
    if (loc.long_name) locationLabelByExternalId.set(externalId, loc.long_name);
  }

  const location_aggs = Array.from(locationAggMap.entries())
    .map(([location_id, amount_to_book]: [string, Decimal]): LocationAgg => ({
      location_id,
      location_label:
        locationLabelByExternalId.get(location_id.trim()) || location_id,
      amount_to_book,
    }))
    .sort((a: LocationAgg, b: LocationAgg) =>
      a.location_label.localeCompare(b.location_label),
    );

  const skipped: SkippedAgg[] = [
    ...Array.from(missingCustomerMap.values()),
    ...Array.from(missingFeeMap.values()),
  ].sort((a: SkippedAgg, b: SkippedAgg) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return a.client_id.localeCompare(b.client_id);
  });

  const summary: ReportSummary = {
    total_input_rows: rows.length,
    unique_customers: uniqueClientIds.size,
    matched_with_fee_count: customer_aggs.length,
    missing_customer_count: missingCustomerMap.size,
    missing_marketing_fee_count: missingFeeMap.size,
  };

  return { customer_aggs, location_aggs, skipped, summary };
}

// ---------------------------------------------------------------------------
// Workbook generation
// ---------------------------------------------------------------------------

async function buildWorkbookBuffer(result: ProcessResult): Promise<Buffer> {
  const composer = new ExcelComposer();
  composer.createSheet("Marketing Fee Accrual");

  // --- Main section: A..E starting row 1 ---
  // Columns: A=Customer/Vendor Name, B=Quantity, C=Settlement, D=Mkt Fee,
  //          E=Amount to Book
  // Row 1 = headers, rows 2..N = data, N+1 = footer SUMs.
  // Amount to Book uses formula =C$ROW * D$ROW (Settlement * MktFee).
  const mainData: CellValue[][] = result.customer_aggs.map(
    (agg: CustomerAgg): CellValue[] => [
      { value: agg.customer_name },
      { value: agg.quantity_sum.toNumber() },
      { value: agg.settlement_sum.toNumber(), format: CellFormat.ACCOUNTING },
      { value: agg.marketing_fee.toNumber(), format: "0.000%" },
      { formula: "=C$ROW*D$ROW", format: CellFormat.ACCOUNTING },
    ],
  );

  composer.addSection(
    {
      headers: [
        "Customer/Vendor Name",
        "Quantity",
        "Settlement",
        "Mkt Fee",
        "Amount to Book",
      ],
      data: mainData,
      footerCalculations: [
        { type: "value", value: "Totals", column: 0, bold: true },
        { type: "sum", column: 1, bold: true, format: CellFormat.NUMBER },
        { type: "sum", column: 2, bold: true, format: CellFormat.ACCOUNTING },
        { type: "value", value: "", column: 3 },
        { type: "sum", column: 4, bold: true, format: CellFormat.ACCOUNTING },
      ],
      styling: {
        header: { bold: true, border: { type: "bottom" } },
        footer: { border: { type: "top" } },
        columns: {
          A: { width: 32 },
          B: { width: 14 },
          C: { width: 18 },
          D: { width: 12 },
          E: { width: 18 },
        },
      },
    } as SectionConfig,
    { startRow: 1, startColumn: 1 },
  );

  // --- Location section: H..I starting row 1 ---
  // Columns: H=Location, I=Sum
  // Pre-computed Amount to Book per Location.
  const locationData: CellValue[][] = result.location_aggs.map(
    (agg: LocationAgg): CellValue[] => [
      { value: agg.location_label },
      { value: agg.amount_to_book.toNumber(), format: CellFormat.ACCOUNTING },
    ],
  );

  composer.addSection(
    {
      headers: ["Location", "Sum"],
      data: locationData,
      footerCalculations: [
        { type: "value", value: "Totals", column: 0, bold: true },
        { type: "sum", column: 1, bold: true, format: CellFormat.ACCOUNTING },
      ],
      styling: {
        header: { bold: true, border: { type: "bottom" } },
        footer: { border: { type: "top" } },
        columns: {
          [numberToExcelColumn(8)]: { width: 28 }, // H
          [numberToExcelColumn(9)]: { width: 18 }, // I
        },
      },
    } as SectionConfig,
    { startRow: 1, startColumn: 8 }, // H
  );

  const buffer = await composer.getBuffer();
  return buffer;
}

// ---------------------------------------------------------------------------
// Tracked report API
// ---------------------------------------------------------------------------

async function createTrackedReport(input: {
  source_filename: string;
}): Promise<CreatedTrackedReport> {
  const body = {
    report_type: REPORT_TYPE,
    files: {
      source: [
        {
          filename: input.source_filename,
          file_type: MimeType.Xlsx,
        },
      ],
      output: [
        {
          filename: OUTPUT_FILENAME,
          file_type: MimeType.Xlsx,
        },
      ],
    },
  };

  const res = await fetchApiPost<CreatedTrackedReport>(
    "tracked/reports",
    body,
  );
  if (!res.success || !res.data) {
    throw new Error(
      `Failed to create tracked report: ${res.message || "unknown error"}`,
    );
  }
  return res.data;
}

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

export function MarketingFeeAccrualReport() {
  return <View />;
}

interface DoneState {
  result: ProcessResult;
  downloadUrl: string;
  downloadFileName: string;
}

function View() {
  const customersRes = useListCustomers();
  const locationsRes = useListLocations();

  const [file, setFile] = useState<File | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [done, setDone] = useState<DoneState | null>(null);

  const customers = customersRes.data?.success
    ? customersRes.data.data
    : undefined;
  const locations = locationsRes.data?.success
    ? locationsRes.data.data
    : undefined;

  const isLoading = customersRes.isLoading || locationsRes.isLoading;
  const loadError =
    !isLoading &&
    (customersRes.error ||
      !customersRes.data?.success ||
      locationsRes.error ||
      !locationsRes.data?.success);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0];
    setFile(next);
    setErrorMessage(null);
    if (done?.downloadUrl) {
      URL.revokeObjectURL(done.downloadUrl);
    }
    setDone(null);
  };

  const onProcess = async () => {
    if (!file || !customers || !locations || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    if (done?.downloadUrl) {
      URL.revokeObjectURL(done.downloadUrl);
      setDone(null);
    }

    try {
      // 1) Parse input
      const inputRows = await parseInputFile(file);

      // 2) Compute aggregates
      const result = processInputRows(inputRows, customers, locations);

      // 3) Create tracked report (gets presigned PUT URLs for source + output)
      const created = await createTrackedReport({
        source_filename: file.name,
      });

      const sourcePresign = created.source[0];
      const outputPresign = created.output[0];
      if (!sourcePresign || !outputPresign) {
        throw new Error(
          "Tracked report response is missing presigned URLs.",
        );
      }

      // 4) Upload source (original input) — abort if this fails.
      const sourceUpload = await UploadPresignedFile(
        sourcePresign.put_url,
        file,
        MimeType.Xlsx,
      );
      if (!sourceUpload.success) {
        throw new Error(
          "Failed to upload source file to S3. Report not generated.",
        );
      }

      // 5) Build the report buffer.
      const buffer = await buildWorkbookBuffer(result);

      // 6) Upload output. If this fails: NO download, NO link, NO blob exposed.
      // (Audit trail: source is already persisted; tracked_report_id exists.)
      const blob = new Blob([buffer], { type: MimeType.Xlsx });
      const outputUpload = await UploadPresignedFile(
        outputPresign.put_url,
        blob,
        MimeType.Xlsx,
      );
      if (!outputUpload.success) {
        throw new Error(
          "Failed to upload generated report to S3. The output was not persisted.",
        );
      }

      // 7) Auto-download + fallback link (in-memory blob URL).
      downloadBinaryData(buffer, OUTPUT_FILENAME, MimeType.Xlsx);
      const downloadUrl = URL.createObjectURL(blob);
      setDone({
        result,
        downloadUrl,
        downloadFileName: OUTPUT_FILENAME,
      });
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : "Failed to process report.";
      console.error("Marketing Fee Accrual report failed", error);
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col space-y-2">
        Failed to load customers or locations. Please refresh and try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="container flex flex-col mb-2">
        <h2 className="scroll-m-20 text-xl font-bold tracking-tight">
          Marketing Fee Accrual
        </h2>
        <p className="text-muted-foreground">
          Upload a single location export and generate a per-customer
          Marketing Fee Accrual report.
        </p>
      </div>
      <div className="container flex flex-col gap-4">
        <div className="grid items-center gap-1.5">
          <Label htmlFor="marketingFeeAccrualInputFile">Input File</Label>
          <Input
            id="marketingFeeAccrualInputFile"
            type="file"
            accept=".xlsx,.xls"
            onChange={onFileChange}
          />
        </div>

        <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
          <Button
            disabled={!file || !customers || !locations || isSubmitting}
            id="processButton"
            onClick={onProcess}
          >
            {isSubmitting ? <Loader /> : "Process"}
          </Button>
        </div>

        {errorMessage && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
            {errorMessage}
          </div>
        )}

        {done && (
          <ReportResultView
            result={done.result}
            downloadUrl={done.downloadUrl}
            downloadFileName={done.downloadFileName}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result view
// ---------------------------------------------------------------------------

function ReportResultView({
  result,
  downloadUrl,
  downloadFileName,
}: {
  result: ProcessResult;
  downloadUrl: string;
  downloadFileName: string;
}) {
  const cards = useMemo(
    (): { name: string; value: string }[] => [
      {
        name: "Total Input Rows",
        value: result.summary.total_input_rows.toLocaleString(),
      },
      {
        name: "Unique Customers",
        value: result.summary.unique_customers.toLocaleString(),
      },
      {
        name: "Matched w/ Marketing Fee",
        value: result.summary.matched_with_fee_count.toLocaleString(),
      },
      {
        name: "Missing Customers",
        value: result.summary.missing_customer_count.toLocaleString(),
      },
      {
        name: "Missing Marketing Fee",
        value: result.summary.missing_marketing_fee_count.toLocaleString(),
      },
    ],
    [result.summary],
  );

  const skippedColumns = useMemo(
    (): DataTableColumn<SkippedAgg>[] => [
      {
        id: "type",
        accessorKey: "type",
        header: "Type",
      },
      {
        id: "client_id",
        accessorKey: "client_id",
        header: "Customer Id",
      },
      {
        id: "customer_name",
        accessorKey: "customer_name",
        header: "Customer Name",
      },
      {
        id: "row_count",
        accessorKey: "row_count",
        header: "# of Rows",
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 flex flex-row items-center gap-3 justify-between">
        <span>
          Report generated and saved. The download should have started
          automatically.
        </span>
        <a
          href={downloadUrl}
          download={downloadFileName}
          className="text-green-900 underline font-medium whitespace-nowrap"
        >
          Download again
        </a>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {cards.map((card: { name: string; value: string }) => (
          <Card key={card.name}>
            <CardContent>
              <div className="flex flex-col items-center gap-2 text-center pt-4">
                <div className="font-light text-muted-foreground text-sm">
                  {card.name}
                </div>
                <div className="font-bold text-lg">{card.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {result.skipped.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Skipped Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              data={result.skipped}
              columns={skippedColumns}
              enableSorting
              enableFiltering
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
